/**
 * Shared analytics helper for ytest.ai products.
 * All calls are safe (no-op if Mixpanel isn't loaded or in development).
 */

function _mp(): any {
  return (window as any).mixpanel
}

/**
 * Identify a user for Mixpanel people profiles.
 * Call once after login so events are tied to the user.
 *
 * When `traits` is provided, the same fields are written in two places:
 *   - `mixpanel.people.set(traits)` — user profile (visible per-user)
 *   - `mixpanel.register(traits)`   — super properties (auto-attached to
 *     every subsequent event so funnels/cohorts can filter on them
 *     without each track() call having to remember to include them)
 *
 * Typical traits: { plan_tier, tenant_id, role, is_trial }.
 */
export function identify(userId: string, traits?: Record<string, any>) {
  try {
    const mp = _mp()
    if (!mp) return
    if (typeof mp.identify === 'function') {
      mp.identify(userId)
    }
    if (traits) {
      if (typeof mp.people?.set === 'function') {
        mp.people.set(traits)
      }
      if (typeof mp.register === 'function') {
        mp.register(traits)
      }
    }
  } catch {
    // Analytics must never break the app
  }
}

/**
 * Clear Mixpanel identity and super properties.
 * Call on logout so the next user on this device doesn't inherit
 * the previous user's distinct_id or super properties.
 */
export function reset() {
  try {
    const mp = _mp()
    if (mp && typeof mp.reset === 'function') {
      mp.reset()
    }
  } catch {
    // Analytics must never break the app
  }
}

/**
 * Track a user event with optional properties.
 * No-op if Mixpanel isn't initialized.
 */
export function track(event: string, properties?: Record<string, any>) {
  try {
    const mp = _mp()
    if (mp && typeof mp.track === 'function') {
      mp.track(event, properties)
    }
  } catch {
    // Analytics must never break the app
  }
}
